import React, { useEffect, useRef, useState } from "react";
import useRevealSequence from "../useRevealSequence";
import Celebration from "../Celebration";

/**
 * Slot Machine - three reels across a payline.
 *
 * The reels run while the round finishes, then stop one after another,
 * left to right, and all three come to rest on the winner. The last reel
 * is the one that pays, so it is given a beat of its own before the
 * payline lights up.
 */

const SYMBOL_H = 150; // a symbol's height inside the window
const LOOPS = 8; // copies of the board stacked in each reel
const REEL_SPEED = 1500; // pixels a second while waiting
const STOP_MS = [350, 550, 750]; // when each reel settles

const easeOutCubic = (p) => 1 - Math.pow(1 - p, 3);

export default function SlotMachine({ tiles, winnerIndex, onFinished }) {
  const count = tiles.length;
  const strip = [];
  for (let loop = 0; loop < LOOPS; loop++) strip.push(...tiles);

  const [offsets, setOffsets] = useState([0, 0, 0]);
  const [landed, setLanded] = useState(-1);
  const offsetsRef = useRef([0, SYMBOL_H * 2, SYMBOL_H * 5]);
  const rafRef = useRef(null);
  // The free spin and the landing both drive the same value, so the free
  // spin has to be told to stop before the landing starts - a frame of
  // the two of them fighting leaves a reel a symbol out of place.
  const freeSpinRef = useRef(true);

  const cycle = count * SYMBOL_H;

  useEffect(() => {
    let last = performance.now();
    const loop = (now) => {
      if (!freeSpinRef.current) return;
      const dt = Math.min(64, now - last);
      last = now;
      offsetsRef.current = offsetsRef.current.map(
        (o) => (o + (dt / 1000) * REEL_SPEED) % cycle,
      );
      setOffsets([...offsetsRef.current]);
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [cycle]);

  useRevealSequence(
    async (ctx) => {
      const target = await ctx.waitForWinner();
      freeSpinRef.current = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);

      // Each reel keeps turning, then eases onto the winning symbol.
      const from = [...offsetsRef.current];
      // The window shows three symbols; the payline is the middle one, so
      // the symbol above the winner is what sits at the top of the window.
      const resting = (((target - 1) * SYMBOL_H) % cycle + cycle) % cycle;
      const started = performance.now();

      // A browser stops calling requestAnimationFrame when its tab or
      // window is not in front, so a landing driven by frames alone hangs
      // there for ever - and a show that never ends never takes itself off
      // the board. The wait below is raced against a plain timer, and the
      // finished state is set either way.
      const spinDown = new Promise((resolve) => {
        const frame = (now) => {
          const elapsed = now - started;
          const next = from.map((f, i) => {
            const p = Math.min(1, elapsed / STOP_MS[i]);
            const turns = cycle * (3 + i); // later reels run longer
            const total = turns + ((resting - f) % cycle + cycle) % cycle;
            return (f + total * easeOutCubic(p)) % cycle;
          });
          offsetsRef.current = next;
          setOffsets(next);
          if (elapsed < STOP_MS[STOP_MS.length - 1]) requestAnimationFrame(frame);
          else resolve();
        };
        requestAnimationFrame(frame);
      });
      await Promise.race([
        spinDown,
        ctx.sleep(STOP_MS[STOP_MS.length - 1] + 400),
      ]);
      offsetsRef.current = [resting, resting, resting];
      setOffsets([resting, resting, resting]);

      await ctx.sleep(100);
      setLanded(target);
      await ctx.sleep(1200);
      onFinished();
    },
    winnerIndex,
    [count, cycle],
  );

  const winner = landed >= 0 ? tiles[landed] : null;

  return (
    <div className="rv-slot">
      <div className={`rv-slot-machine ${landed >= 0 ? "rv-slot-machine--won" : ""}`}>
        <div className="rv-slot-window">
          <div className="rv-slot-payline" />
          {[0, 1, 2].map((reel) => (
            <div className="rv-slot-reel" key={reel}>
              <div
                className="rv-slot-strip"
                style={{ transform: `translateY(${-offsets[reel]}px)` }}
              >
                {strip.map((tile, i) => (
                  <div className="rv-slot-symbol" key={`${reel}-${i}`}>
                    <img src={tile.img} alt={tile.name || ""} />
                    <span>{tile.name}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="rv-slot-lever" />
      </div>

      {winner ? <Celebration tile={winner} seed={landed} /> : null}
    </div>
  );
}
